import { Injectable, inject } from '@angular/core';
import { CuriosityUtil, STORAGE_KEYS_CONSTANTS } from '../constants';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { IAuthToken, ICredentials, IUser } from '../models';
import { HttpClient } from '@angular/common/http';
import { AuthenticateEndpoint } from '../endpoints/authenticate.';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Checkout } from './checkout';
import { UserEndpoint } from '@core/endpoints';
import { Storage } from './storage';

// import { Alert } from './alert';

/**
 * Servicio de autenticación
 */
@Injectable({
  providedIn: 'root',
})
export class Authentication {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly storage = inject(Storage);
  /**
   * observable que se dispara cuando cierran sesión
   */
  onLogout: Subject<any> = new Subject();

  /**
   * observable que se activa cuando la autenticación se ha hecho con exito
   */
  onAuthenticate: BehaviorSubject<boolean> = new BehaviorSubject(this.isAuthenticated());
  /** Variable BehaviorSubject que guarda la información del usuario cuando es actualizada*/
  onUpdateUser: BehaviorSubject<IUser | null> = new BehaviorSubject(this.getAuthInformation());
  /** Variable de tipo BehaviorSubject que guarda el valor del formulario si es abierno o no*/
  openLoginForm: Subject<any> = new Subject();

  /** @ignore*/
  constructor() {
    this.onUpdateUser.subscribe((user) => {
      this.setLoggedUserSession(user);
      // if (user) {
      //   setTimeout(() => this.setCheckoutStorageFromUser(Number(user.id)));
      // }
    });
  }

  /**
   * @returns KEY_TOKEN_STORAGE: Retorna el valor de token que esta en el localStorage
   */
  getToken(): string {
    return this.storage.getLocal(STORAGE_KEYS_CONSTANTS.TOKEN) || '';
  }

  /**
   * @returns KEY_REFRESH_TOKEN_STORAGE: Retorna el valor del refresh token que esta en el localStorage
   */
  getRefreshToken(): string {
    return this.storage.getLocal(STORAGE_KEYS_CONSTANTS.REFRESH_TOKEN) || '';
  }

  /**
   * @returns un JSON con el token, solo si esta autenticado
   */
  isAuthenticated(): boolean {
    const authInfo = this.storage.getLocal(STORAGE_KEYS_CONSTANTS.AUTHENTICATION);
    return !!authInfo;
  }

  /**
   * @returns KEY_AUTHENTICATION_STORAGE: Retorna una JSON con el valor del token de autenticación que esta guardado en el localStorage
   */
  getAuthInformation(): IUser | null {
    const authInfo = this.storage.getLocal(STORAGE_KEYS_CONSTANTS.AUTHENTICATION);
    return authInfo || null;
  }

  /**
   * @param token: valor el cual es pasado por parametro y sera asociado al KEY_TOKEN_STORAGE
   * esta funcion envia al localStorage la clave token junto con su valor para su respectivo guardado
   */
  private setTokenSession(token: string): void {
    this.storage.setLocal(STORAGE_KEYS_CONSTANTS.TOKEN, token);
  }

  /**
   * @param refreshToken: valor el cual es pasado por parametro y sera asociado al KEY_REFRESH_TOKEN_STORAGE
   * esta funcion envia al localStorage la clave refresh_token junto con su valor para su respectivo guardado
   */
  private setRefreshTokenSession(refreshToken: string): void {
    this.storage.setLocal(STORAGE_KEYS_CONSTANTS.REFRESH_TOKEN, refreshToken);
  }

  /**
   * Metodo para enviar la sesión del usuario registrado
   * @param user: Recibe por parametro información del usuario,
   * esta tendra la estructura de la interface IUser
   * La función se encarga de enviar al localStorage la información del usuario
   * que acaba de iniciar sesión, para esto hace uso de la clave KEY_AUTHENTICATION_STORAGE
   */
  private setLoggedUserSession(user: IUser | null): void {
    this.storage.setLocal(STORAGE_KEYS_CONSTANTS.AUTHENTICATION, JSON.stringify(user));
    if (user) {
      // si hay información de usuario, envia el estado (true:false) para saber si debe mostrar o no las curiosidades
      CuriosityUtil.setCuriosity(user.show_curiosity);
    }
  }

  /**
   * Metodo para autenticar al usuario, envia la sesion si es activada
   * @param {ICredentials} credentials: informacion de typo ICredencial (email y password) para la autenticación
   * @returns Valor del token guardado en el almacenamiento local
   */
  authenticate(credentials: ICredentials): Observable<IAuthToken> {
    return this.http.post<IAuthToken>(AuthenticateEndpoint.authenticate, credentials).pipe(
      tap((response) => {
        this.setTokenSession(response.access);
        this.setRefreshTokenSession(response.refresh);
      }),
    );
  }

  /**
   * Method to refresh the token
   * @param token : token value stored in local storage or localStorage
   * @returns {IAuthToken}: IAuthToken object representing the newly generated token
   */
  refresh(token: string): Observable<IAuthToken> {
    return this.http.post<IAuthToken>(AuthenticateEndpoint.refreshToken, { refresh: token }).pipe(
      tap((response) => {
        this.setTokenSession(response.access);
        // Actualizar refresh token si el backend lo devuelve
        if (response.refresh) {
          this.setRefreshTokenSession(response.refresh);
        }
      }),
    );
  }

  /**
   * Metodo para obtener al usuario logueado
   * @param {string} token: valor del token guardado en el almacenamiento local o localStorage
   * @returns {IUser}: Objeto de tipo IUser que representa al usuario que inicio sesión
   */
  getLoggedUser(token: string): Observable<IUser> {
    return this.http.post<IUser>(AuthenticateEndpoint.getAuthInfo, { token }).pipe(
      tap((auth) => {
        this.onUpdateUser.next(auth);
        this.onAuthenticate.next(true);
      }),
    );
  }

  /**
   * Metodo que finaliza la sesión
   * en este metodo se limpia el alamecenamiento local, se procede a terminar la
   * sesión de un usuario y a cambiar el estado de autenticación a false
   */
  logout(): void {
    this.storage.clearLocal();
    this.onLogout.next(true);
    this.onAuthenticate.next(false);
    setTimeout(() => this.router.navigate(['']), 50);
  }

  /**
   * Method to set the checkout storage from the user
   * @param userId : logged in user id
   */
  // async setCheckoutStorageFromUser(userId: number): Promise<void> {
  //   try {
  //     await this._checkout.setCheckoutStorageFromUser(userId);
  //   } catch (error) {
  //     console.error(error);
  //   }
  // }

  loginWithSocialApi(access: string): Observable<IUser> {
    this.setTokenSession(access);

    return this.http
      .post<IUser>(
        UserEndpoint.userApi,
        {},
        {
          headers: {
            Authorization: `Bearer ${access}`,
          },
        },
      )
      .pipe(
        tap((user) => {
          this.onUpdateUser.next(user);
          this.onAuthenticate.next(true);
        }),
      );
  }
}
